import {
  DialogContainer,
  Dialog,
  Content,
  Heading,
  Divider,
  Text,
  ButtonGroup,
  Button,
  AlertDialog,
  Flex,
} from "@adobe/react-spectrum";
import { Alert } from "../common/Icons";

type SoundState = "allow" | "deny" | "automatic" | "unknown";

type UnifiedDialogContainerProps = {
  showPermPopup: boolean;
  onMicContinue: () => void;
  isTeamsContext?: boolean;

  showNotificationPermissionDialog: boolean;
  onNotificationTest: () => void;
  onNotificationAcknowledge: () => void;

  showSoundDialog: boolean;
  soundState: SoundState;
  onSoundTest: () => void;
  onSoundClose: () => void;

  showCCPPermPopup: boolean;
  onCCPAllow: () => void;

  showLogoutDialog: boolean;
  onLogoutOk: () => void;

  showNotification: boolean;
  notification: any;
  onNotificationClose: () => void;

  userGuideUrl: string;
};

const UnifiedDialogContainer = ({
  showPermPopup,
  onMicContinue,
  isTeamsContext = false,
  showNotificationPermissionDialog,
  onNotificationTest,
  onNotificationAcknowledge,
  showSoundDialog,
  soundState,
  onSoundTest,
  onSoundClose,
  showCCPPermPopup,
  onCCPAllow,
  showLogoutDialog,
  onLogoutOk,
  showNotification,
  notification,
  onNotificationClose,
  userGuideUrl,
}: UnifiedDialogContainerProps) => {
  const active = showPermPopup
    ? "mic"
    : showSoundDialog
    ? "sound"
    : showNotificationPermissionDialog
    ? "notif"
    : showCCPPermPopup
    ? "ccp"
    : showLogoutDialog
    ? "logout"
    : showNotification
    ? "alert"
    : null;

  if (!active) return null;

  const onDismiss = active === "alert" ? () => onNotificationClose() : () => {};

  return (
    <DialogContainer onDismiss={onDismiss}>
      {active === "mic" && (
        <Dialog width={"500px"}>
          <Heading level={4}>
            <Flex justifyContent={"space-between"}>
              <span>Microphone permission required!</span>
              <span style={{ marginRight: "15px" }}>
                <Alert />
              </span>
            </Flex>
          </Heading>
          <Divider />
          <Content>
            <Text>
              This app requires microphone access for call functionality. Please
              enable microphone permission and click "Continue".
            </Text>
            {isTeamsContext && (
              <>
                <br />
                <br />
                <Text>
                  In Microsoft Teams, also allow microphone access for iHub:
                  open Teams Settings, go to App permissions, select iHub, and
                  enable Microphone.
                </Text>
              </>
            )}
            <br />
            <br />
            <span>
              If you are unable to find microphone setting, you can use{" "}
            </span>
            <a href={userGuideUrl} target="_blank" rel="noreferrer">
              this guide to enable mic access
            </a>
          </Content>
          <ButtonGroup>
            <Button variant="cta" onPress={onMicContinue} autoFocus>
              Continue
            </Button>
          </ButtonGroup>
        </Dialog>
      )}

      {active === "notif" && (
        <Dialog width={"500px"}>
          <Heading level={4}>
            <Flex justifyContent={"start"} alignItems={"center"}>
              🔔
              <span style={{ marginLeft: "10px" }}>
                Don’t miss incoming contacts
              </span>
            </Flex>
          </Heading>
          <Divider />
          <Content>
            <p>
              Allow browser notifications so you can accept contacts even when
              this tab isn't active.
            </p>
            <p>Notifications may still be missed in these cases:</p>
            <ul className="ihub-notifications-bullets">
              <li>
                On <strong>Mac/Windows</strong>, Focus/Do Not Disturb can hide
                alerts.
              </li>
              <li>
                During Teams calls, browser system notifications can be
                suppressed
              </li>
            </ul>
            <p>
              <strong>How to fix it:</strong>
            </p>
            <ul className="ihub-notifications-bullets">
              <li>
                <strong>Browser settings:</strong> Site settings → Notifications
                → Allow
              </li>
              <li>
                <strong>System settings:</strong> Enable notifications for your
                browser in your system's notification settings
              </li>
              <li>
                <strong>On Mac/Windows:</strong> Turn off Focus/Do Not Disturb
                during work hours
              </li>
              <li>
                <strong>Teams Focus conflict:</strong> Teams can control your
                system Focus mode - check your system's Focus settings to ensure
                Teams isn't blocking notifications
              </li>
              <li>
                <strong>Test it:</strong> Send a test notification below to
                verify
              </li>
            </ul>
            <br />
            <Button variant="cta" onPress={onNotificationTest} autoFocus>
              Send test notification
            </Button>
            <p>
              If you are unable to find notification settings, you can use{" "}
              <a href={userGuideUrl} target="_blank" rel="noreferrer">
                this guide to enable notification access.
              </a>
            </p>
          </Content>
          <ButtonGroup>
            <Button variant="cta" onPress={onNotificationAcknowledge} autoFocus>
              Got it
            </Button>
          </ButtonGroup>
        </Dialog>
      )}

      {active === "sound" && (
        <Dialog width={"550px"}>
          <Heading level={4}>
            <Flex justifyContent={"start"} alignItems={"center"}>
              🔊
              <span style={{ marginLeft: "10px" }}>
                Check your site sound setting
              </span>
            </Flex>
          </Heading>
          <Divider />
          <Content>
            <p>
              For the best experience with incoming call notifications, we recommend setting 
              your browser sound permission to "Allow". This helps ensure you never miss a contact,
              especially after periods of inactivity.
            </p>
            
            <p><strong>Recommended settings:</strong></p>
            <ul className="ihub-notifications-bullets">
              <li>
                <strong>Chrome:</strong> Click the tune icon (⚙️) in the address bar → 
                Site settings → Sound → Select <strong>"Allow"</strong>
              </li>
              <li>
                <strong>Edge:</strong> Click the lock icon (🔒) in the address bar → 
                Permissions for this site → Media autoplay → Select <strong>"Allow"</strong>
              </li>
              <li>
                <strong>System volume:</strong> Ensure your computer's output device and volume are properly configured
              </li>
              <li>
                <strong>Use external headphones:</strong> For best call quality and to avoid voice quality issues, 
                always use external headphones/headset instead of built-in speakers and microphone
              </li>
              <li>
                <strong>Test it:</strong> Use the button below to verify your sound is working
              </li>
            </ul>
            <br />
            <Button variant="cta" onPress={onSoundTest} autoFocus>
              Play test sound
            </Button>
            <p>
              For detailed instructions, see{" "}
              <a href={userGuideUrl} target="_blank" rel="noreferrer">
                this guide.
              </a>
            </p>
          </Content>
          <ButtonGroup>
            <Button variant="cta" onPress={onSoundClose} autoFocus>
              Got it
            </Button>
          </ButtonGroup>
        </Dialog>
      )}

      {active === "ccp" && (
        <Dialog>
          <Heading level={4}>
            <span>
              <span style={{ marginRight: "15px" }}>
                <Alert />
              </span>
              <span>Permission Required</span>
            </span>
          </Heading>
          <Divider />
          <Content>
            <Text>
              This app will not work unless microphone permission is allowed.
              Click the button below to continue.
            </Text>
            {isTeamsContext && (
              <>
                <br />
                <br />
                <Text>
                  In Microsoft Teams, also enable microphone access for iHub
                  under Settings → App permissions.
                </Text>
              </>
            )}
          </Content>
          <ButtonGroup>
            <Button variant="cta" onPress={onCCPAllow} autoFocus>
              Allow
            </Button>
          </ButtonGroup>
        </Dialog>
      )}

      {active === "logout" && (
        <Dialog>
          <Heading level={4}>
            <span>
              <span style={{ marginRight: "15px" }}>
                <Alert />
              </span>
              <span>Logged out</span>
            </span>
          </Heading>
          <Divider />
          <Content>
            <Text>
              You were logged out due to network disconnect. Please login again
              to continue
            </Text>
          </Content>
          <ButtonGroup>
            <Button variant="cta" onPress={onLogoutOk} autoFocus>
              ok
            </Button>
          </ButtonGroup>
        </Dialog>
      )}

      {active === "alert" && (
        <AlertDialog
          title={notification?.title || "Network error!"}
          variant={notification?.type || "information"}
          primaryActionLabel="Ok"
          onPrimaryAction={onNotificationClose}
        >
          {notification?.message}
        </AlertDialog>
      )}
    </DialogContainer>
  );
};

export default UnifiedDialogContainer;

